import type { GetCurrentMemberFn, SearchMembersFn } from '@/features/iam'
import { type Job } from '@/features/jobs/Job'
import { action, computed, observable } from 'mobx'
import { required, validationContext } from 'validx'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { type Member } from '@/features/iam/members/Member'
import { Id } from '@/infra/id'

/**
 * Picks the Client a job is transferred to.
 */
export class ClientInputEditorViewModel {
  /**
   * The client the jobs are transferred to.
   */
  @observable
  accessor client: Member | undefined = undefined

  /**
   * The jobs being transferred.
   */
  @observable
  accessor jobs: Job[] = []

  /**
   * Validation context for client.
   */
  readonly validationClient = validationContext(this, {
    client: [required('Client is required')],
  })

  constructor(
    private readonly _findMembers: SearchMembersFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {}

  /**
   * Getter for the current job client.
   */
  @computed
  get currentClient() {
    return this.jobs[0].owner
  }

  /**
   * Sets client to transfer.
   */
  @action.bound
  setClient(client?: Member) {
    this.client = client
  }

  /**
   * Indicates if client is valid.
   */
  @action.bound
  validateClient() {
    return this.validationClient.reset().validate().isValid
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.validationClient.reset()
    this.client = undefined
  }

  /**
   * Sets the jobs being transferred.
   */
  @action.bound
  setJobs(jobs: Job[]) {
    this.jobs = jobs
  }

  /**
   * Fetches clients.
   */
  fetchClients = async (
    searchText: string,
    params: Partial<CursorPageQuery>,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Member>> => {
    const collaborators = this.jobs.map((j) => j.collaborators.items).flat()
    const paged = await this._findMembers(
      {
        after: params.after,
        limit: params.limit,
        workspaceId: Id.assert(
          this._getCurrentMember(this.jobs[0].workspace.id).workspace.id,
        ),
        searchText: searchText,
      },
      signal,
    )

    return CursorPaged.map(paged, (data) =>
      data.filter((m) => {
        const invalidCandidate =
          collaborators.some((s) => s.member === m) ||
          m.user.id === 'SYSTEM_ROBOT'
        return !invalidCandidate
      }),
    )
  }
}
